import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useEntityRouter } from '../hooks/useEntityRouter';
import { Box, Button, IconButton, Stack, TextField, MenuItem, Switch, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listWorkers,
  createWorker,
  updateWorker,
  deleteWorker,
  Worker,
  WorkerCreate,
  // getWorker_transportTypes,
} from '../services/workerService';
import { TransportType, WorkerType, MerchandiserType } from '../constants/entities';
import DataTable from '../components/DataTable';
import FormDialog from '../components/FormDialog';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import WeeklyWorkSchedule, { WeeklyWorkScheduleValue } from '../components/WeeklyWorkSchedule';
import LocationInput from '../components/LocationInput';
import BusinessTypeMultiSelect from '../components/BusinessTypeMultiSelect';
import { listBusinessTypes, BusinessType } from '../services/businessTypeService';
import axios from 'axios';
import {DayPlan} from "@components/MonthlyPlanTable.tsx";

// Patch Worker type for availability_plan and work_schedule
interface WorkerWithPlan extends Worker {
  availability_plan?: string | DayPlan[];
  work_schedule?: string | WeeklyWorkScheduleValue;
  restricted_business_type_ids: number[];
}

const WorkersPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { entityId, navigateToEntity, navigateToList } = useEntityRouter('/workers');

  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState<WorkerWithPlan | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [_transportTypes] = useState<string[]>([]);
  const [_availabilityPlan, _setAvailabilityPlan] = useState<DayPlan[]>([]);
  const [workSchedule, setWorkSchedule] = useState<WeeklyWorkScheduleValue>({ weekly: {}, holidays: [] });
  const [addressDialog, setAddressDialog] = useState<{ open: boolean; address: string; lat: number; lng: number } | null>(null);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const workerTypeOptions = [
    { value: 'regular', label: 'Regular' },
    { value: 'manager', label: 'Manager' },
  ];
  const merchandiserTypeOptions = [
    { value: 'visiting', label: 'Visiting' },
    { value: 'part_time', label: 'Part Time' },
  ];

  // Dynamically create schema based on _transportTypes
  const schema = useMemo(() =>
    yup.object({
      name: yup.string().required(),
      email: yup.string().email().required('Email is required'),
      transport_type: yup.string().oneOf(_transportTypes.length ? _transportTypes : ['auto', 'public transport', 'public+scooters']).required(),
      location: yup.object({
        address: yup.string().required(),
        lat: yup.number().required(),
        lng: yup.number().required(),
      }).required(),
      pricing: yup.number().positive().required(),
      restricted_business_type_ids: yup.array().of(yup.number()).required(),
      worker_type: yup.string().oneOf(workerTypeOptions.map(o => o.value)).required(),
      manager_type: yup.string().required(),
      merchandiser_type: yup.string().oneOf(merchandiserTypeOptions.map(o => o.value)).required(),
    }).required(),
    [_transportTypes]
  );

  type FormValues = yup.InferType<typeof schema>;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      transport_type: 'auto',
      location: { address: '', lat: 0, lng: 0 },
      pricing: 0,
      restricted_business_type_ids: [],
      worker_type: 'regular',
      manager_type: '',
      merchandiser_type: 'visiting',
    },
  });

  const { data: workers = [], isLoading } = useQuery<Worker[]>({ 
    queryKey: ['workers'], 
    queryFn: () => listWorkers() 
  });

  // Load specific worker if entityId is present
  useEffect(() => {
    if (entityId && workers.length > 0) {
      const worker = workers.find((w: Worker) => w.id === parseInt(entityId));
      if (worker) {
        handleEdit(worker as WorkerWithPlan);
      }
    }
  }, [entityId, workers]);

  const createMut = useMutation({
    mutationFn: createWorker,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workers'] }),
  });
  const updateMut = useMutation({
    mutationFn: (payload: Worker) => updateWorker(payload.id!, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workers'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteWorker(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workers'] }),
  });
  
  const toggleAvailabilityMut = useMutation({
    mutationFn: ({ worker, isAvailable }: { worker: Worker; isAvailable: boolean }) => 
      updateWorker(worker.id!, { ...worker, is_available: isAvailable }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workers'] }),
  });

  useEffect(() => {
    // getWorker_transportTypes().then(set_transportTypes);
    listBusinessTypes().then(setBusinessTypes);
  }, []);

  const onSubmit = (vals: FormValues) => {
    const { location, ...rest } = vals;
    const submitVals = {
      ...rest,
      starting_location: location.address,
      starting_location_lat: location.lat,
      starting_location_lng: location.lng,
      work_schedule: JSON.stringify(workSchedule),
      availability_hours: {},
      restricted_business_type_ids: (vals.restricted_business_type_ids ?? []).filter((v): v is number => typeof v === 'number'),
    };
    if (editData) {
      updateMut.mutate({ ...editData, ...submitVals, transport_type: submitVals.transport_type as TransportType, availability_hours: editData.availability_hours, worker_type: submitVals.worker_type as WorkerType, merchandiser_type: submitVals.merchandiser_type as MerchandiserType });
    } else {
      const createData: WorkerCreate = {
        ...submitVals,
        transport_type: submitVals.transport_type as TransportType,
        worker_type: submitVals.worker_type as WorkerType,
        merchandiser_type: submitVals.merchandiser_type as MerchandiserType,
        phone: null,
        is_available: true,
        settlement_id: null,
        manager_id: null,
        supervisor_id: null,
        default_timings: {},
      };
      createMut.mutate(createData);
    }
    setOpenForm(false);
    // Navigate back to list view after saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    // Navigate back to list view when closing without saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleAdd = () => {
    reset({ name: '', email: '', transport_type: 'auto', location: { address: '', lat: 0, lng: 0 }, pricing: 0, restricted_business_type_ids: [], worker_type: WorkerType.REGULAR, manager_type: '', merchandiser_type: MerchandiserType.VISITING });
    setWorkSchedule({ weekly: {}, holidays: [] });
    setEditData(null);
    setOpenForm(true);
  };

  const handleEdit = (row: WorkerWithPlan) => {
    let schedule: WeeklyWorkScheduleValue = { weekly: {}, holidays: [] };
    if (row.work_schedule) {
      try {
        schedule = typeof row.work_schedule === 'string' ? JSON.parse(row.work_schedule) : row.work_schedule;
      } catch { schedule = { weekly: {}, holidays: [] }; }
    }
    setEditData(row);
    reset({
      name: row.name,
      email: row.email,
      transport_type: row.transport_type as TransportType,
      location: { address: row.starting_location || '', lat: row.starting_location_lat || 0, lng: row.starting_location_lng || 0 },
      pricing: row.pricing ?? undefined,
      restricted_business_type_ids: Array.isArray(row.restricted_business_type_ids) ? row.restricted_business_type_ids.filter((v): v is number => typeof v === 'number') : [],
      worker_type: row.worker_type || WorkerType.REGULAR,
      manager_type: row.manager_type || '',
      merchandiser_type: row.merchandiser_type || MerchandiserType.VISITING,
    });
    setWorkSchedule(schedule);
    setOpenForm(true);
  };

  const handleRowClick = (row: Worker) => {
    navigateToEntity(row.id);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post('/api/v1/workers/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: ['workers'] });
      alert('Workers uploaded successfully!');
    } catch (err) {
      alert('Failed to upload CSV');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: t('workers.name'), flex: 1 },
      { field: 'email', headerName: t('workers.email'), flex: 1 },
      { field: 'transport_type', headerName: t('workers.transport'), width: 120 },
      { field: 'worker_type', headerName: 'Worker Type', width: 120 },
      { field: 'manager_type', headerName: 'Manager Type', width: 120 },
      { field: 'merchandiser_type', headerName: 'Merchandiser Type', width: 150 },
      {
        field: 'is_available',
        headerName: t('workers.status'),
        width: 120,
        renderCell: (params: any) => (
          <FormControlLabel
            control={
              <Switch
                checked={params.row.is_available ?? true}
                onChange={(e) => {
                  toggleAvailabilityMut.mutate({
                    worker: params.row,
                    isAvailable: e.target.checked
                  });
                }}
                size="small"
              />
            }
            label=""
          />
        ),
      },
      {
        field: 'starting_location',
        headerName: 'Start',
        flex: 1,
        renderCell: (params: any) => (
          <Button variant="text" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => setAddressDialog({ open: true, address: params.row.starting_location, lat: params.row.starting_location_lat, lng: params.row.starting_location_lng })}>
            {params.row.starting_location || '-'}
          </Button>
        ),
      },
      { field: 'pricing', headerName: 'Pricing', width: 120 },
      {
        field: 'restricted_business_type_ids',
        headerName: 'Restricted Business Types',
        flex: 1,
        valueGetter: (params: any) => {
          if (!params || !params.row) return '-';
          if (Array.isArray(params.row.restricted_business_type_ids) && params.row.restricted_business_type_ids.length > 0) {
            return params.row.restricted_business_type_ids
              .map((id: number) => businessTypes.find((bt) => bt.id === id)?.name || id)
              .join(', ');
          }
          return '-';
        },
      },
      {
        field: 'actions',
        headerName: t('workers.actions'),
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteId(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [businessTypes]
  );

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Workers</h2>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('workers.add')}
          </Button>
          <Button variant="outlined" onClick={handleUploadClick} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Stack>
      </Stack>

      <DataTable 
        rows={workers} 
        columns={columns} 
        loading={isLoading} 
        onRowDoubleClick={handleEdit}
        onRowClick={handleRowClick}
      />

      <FormDialog open={openForm} title={editData ? t('workers.edit') : t('workers.add')} onClose={handleCloseForm} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} pt={1}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField label="Name" {...field} fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField label="Email" {...field} fullWidth error={!!errors.email} helperText={errors.email?.message} />
            )}
          />
          <Controller
            name="transport_type"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Transport Type"
                {...field}
                fullWidth
                error={!!errors.transport_type}
                helperText={errors.transport_type?.message}
              >
                {_transportTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <LocationInput
                address={field.value.address}
                lat={field.value.lat}
                lng={field.value.lng}
                onChange={field.onChange}
                compact
                markerLabel={control._formValues?.name || ''}
              />
            )}
          />
          <Controller
            name="pricing"
            control={control}
            render={({ field }) => (
              <TextField label="Pricing" type="number" {...field} fullWidth error={!!errors.pricing} helperText={errors.pricing?.message} />
            )}
          />
          <Controller
            name="restricted_business_type_ids"
            control={control}
            render={({ field }) => (
              <BusinessTypeMultiSelect
                value={(field.value ?? []).filter((v: unknown): v is number => typeof v === 'number')}
                onChange={field.onChange}
                options={businessTypes}
                error={!!errors.restricted_business_type_ids}
                helperText={errors.restricted_business_type_ids?.message}
                label="Restricted Business Types"
              />
            )}
          />
          <Controller
            name="worker_type"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Worker Type"
                {...field}
                fullWidth
                error={!!errors.worker_type}
                helperText={errors.worker_type?.message}
              >
                {workerTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="manager_type"
            control={control}
            render={({ field }) => (
              <TextField
                label="Manager Type"
                {...field}
                fullWidth
                error={!!errors.manager_type}
                helperText={errors.manager_type?.message}
              />
            )}
          />
          <Controller
            name="merchandiser_type"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Merchandiser Type"
                {...field}
                fullWidth
                error={!!errors.merchandiser_type}
                helperText={errors.merchandiser_type?.message}
              >
                {merchandiserTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
          <WeeklyWorkSchedule value={workSchedule} onChange={setWorkSchedule} holidayMode="single" />
        </Stack>
      </FormDialog>

      {addressDialog && (
        <FormDialog open={addressDialog.open} title="Worker Start Location" onClose={() => setAddressDialog(null)} onSubmit={() => setAddressDialog(null)} submitLabel="Close">
          <LocationInput
            address={addressDialog.address}
            lat={addressDialog.lat}
            lng={addressDialog.lng}
            onChange={() => {}}
            compact
            disabled
            markerLabel={editData?.name || ''}
          />
        </FormDialog>
      )}

      <ConfirmDeleteDialog
        open={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMut.mutate(deleteId);
          setDeleteId(null);
        }}
      />
    </Box>
  );
};

export default WorkersPage; 